<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { api } from "@/api";
import type { Team } from "@shared/types";
import PotFilter from "@/components/admin/PotFilter.vue";
import TeamRow from "@/components/admin/TeamRow.vue";
import SaveBar from "@/components/admin/SaveBar.vue";
import Toast from "@/components/common/Toast.vue";

const original = ref<Map<string, Team>>(new Map());
const drafts = reactive<Record<string, Team>>({});
const invalidIds = ref<Set<string>>(new Set());
const loading = ref(true);
const saving = ref(false);
const filter = ref<0 | 1 | 2 | 3>(0);
const toast = ref<{ kind: "success" | "error"; message: string } | null>(null);
const loadError = ref<string | null>(null);

const teams = computed(() => Object.values(drafts).sort((a, b) => a.pot - b.pot || a.name.localeCompare(b.name)));
const filteredTeams = computed(() => filter.value === 0 ? teams.value : teams.value.filter(t => t.pot === filter.value));

function isDirty(team: Team): boolean {
  const orig = original.value.get(team.id);
  if (!orig) return false;
  const keys: Array<keyof Team> = ["wins", "draws", "goals_for", "goals_against", "clean_sheets", "yellows", "reds", "round_reached", "eliminated"];
  return keys.some(k => team[k] !== orig[k]);
}

const dirtyCount = computed(() => teams.value.filter(isDirty).length);

function update(id: string, field: keyof Team, value: number) {
  if (drafts[id]) {
    (drafts[id] as unknown as Record<string, unknown>)[field as string] = value;
    if (invalidIds.value.has(id)) {
      const next = new Set(invalidIds.value);
      next.delete(id);
      invalidIds.value = next;
    }
  }
}

function discard() {
  for (const [id, team] of original.value) drafts[id] = { ...team };
  invalidIds.value = new Set();
}

async function load() {
  try {
    const res = await api.adminTeams();
    original.value = new Map(res.teams.map(t => [t.id, { ...t }]));
    for (const t of res.teams) drafts[t.id] = { ...t };
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

async function save() {
  const updates = teams.value.filter(isDirty).map(t => ({
    id: t.id,
    wins: t.wins, draws: t.draws, goals_for: t.goals_for, goals_against: t.goals_against,
    clean_sheets: t.clean_sheets, yellows: t.yellows, reds: t.reds, round_reached: t.round_reached, eliminated: t.eliminated,
  }));
  if (!updates.length) return;
  saving.value = true;
  try {
    await api.saveTeams({ updates });
    for (const u of updates) {
      const team = drafts[u.id];
      if (team) original.value.set(u.id, { ...team });
    }
    toast.value = { kind: "success", message: `Saved ${updates.length} team${updates.length === 1 ? '' : 's'}` };
  } catch (e) {
    const err = e as { apiError?: { offendingIds?: string[]; message?: string } };
    if (err.apiError?.offendingIds?.length) {
      invalidIds.value = new Set(err.apiError.offendingIds);
      toast.value = { kind: "error", message: "Couldn't save — check highlighted rows" };
    } else {
      toast.value = { kind: "error", message: err.apiError?.message ?? "Save failed — try again" };
    }
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
    e.preventDefault();
    if (dirtyCount.value > 0 && !saving.value) save();
  }
}

onMounted(() => { load(); window.addEventListener("keydown", onKeydown); });
onUnmounted(() => window.removeEventListener("keydown", onKeydown));
</script>

<template>
  <main class="max-w-6xl mx-auto px-4 py-6">
    <header class="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div class="flex items-center gap-2">
        <span class="text-2xl" aria-hidden="true">⚙️</span>
        <h1 class="text-2xl font-extrabold tracking-tight text-slate-900">Admin · Team Results</h1>
      </div>
      <PotFilter v-model:value="filter" />
    </header>

    <p v-if="loading" class="text-slate-500">Loading…</p>
    <p v-else-if="loadError" class="text-red-600">{{ loadError }}</p>

    <div v-else class="overflow-x-auto rounded-xl ring-1 ring-slate-200 bg-white shadow-sm">
      <table class="min-w-full text-sm">
        <thead class="sticky top-0 bg-slate-50 text-slate-500 border-b border-slate-200">
          <tr>
            <th class="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-xs">Team</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">W</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">D</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">GF</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">GA</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">CS</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">Y</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">R</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">Round</th>
            <th class="px-2 py-2.5 font-semibold uppercase tracking-wider text-xs">Out</th>
            <th class="px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-xs">Pts</th>
          </tr>
        </thead>
        <tbody>
          <TeamRow
            v-for="t in filteredTeams"
            :key="t.id"
            :team="original.get(t.id)!"
            :draft="t"
            :dirty="isDirty(t)"
            :invalid="invalidIds.has(t.id)"
            :saving="saving"
            @update="(field, value) => update(t.id, field, value)"
          />
        </tbody>
      </table>
    </div>

    <SaveBar :dirty-count="dirtyCount" :saving="saving" @save="save" @discard="discard" />

    <Toast v-if="toast" :kind="toast.kind" :message="toast.message" @dismiss="toast = null" />
  </main>
</template>
